import React, { FC, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  HorizontalTabs,
  HorizontalTab,
  Button,
  ListItem,
  Text,
  Tooltip,
} from '@cubeyou-team/hx-react-components';
import styled from 'styled-components';

import { isAvailableAddon } from 'src/utils/AuthHelperMethods';
import { supportsSegmentationAndCompare } from 'src/utils/features';
import { ALL_QUERY_BUILDER_TAB_COLORS } from 'src/modules/query-builder/constants';
import { TrialRestricted, TRIAL_FEATURES } from 'src/modules/trial-paywall';
import {
  addNewTarget,
  changeTargetColor,
  removeTarget,
  changeTargetTitle,
  changeTargetSelected,
} from '../../../../actions';
import { isEmpty, numberWithCommas } from '../../../../utils/misc';
import QueryTarget from './components/QueryTarget';
import QueryTargetTitle from './components/QueryTargetTitle';
import { getTargets, getTargetsIds } from '../../../../selectors/queryBuilder';
import { APPS_MAP } from '../../../../constants/config';

const StyledTabActionColor = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`;

const StyledButton = styled(Button)`
  & > i > * {
    height: 20px;
    width: 20px;
  }
`;

const StyledColorCircle = styled.div`
  width: 20px;
  height: 20px;
  background: ${(props) => props.color};
  border-radius: 50%;
  flex: 0 0 auto;
  margin-right: 16px;
`;

const StyledColorLabel = styled.div`
  flex: 1 0 auto;
`;

const StyledConversationTarget = styled.div`
  background: #ffffff;
  padding: 24px 0;
  overflow: auto !important;
  height: calc(100% - 65px) !important;
`;

interface QueryTargetsProps {
  onItemDragToggle: (_value) => void;
  onItemDropped: () => void;
  onRequireMissingItemRequest: () => void;
  onQueryPreviewRequest: () => void;
  entityDragging: any;
  isQueryPreviewVisible: boolean;
  isStanleyQueryPanelVisible: boolean;
  isStanleyHistoryView: boolean;
  onStanleyQueryPanelToggle: () => void;
  onOpenStanleyHistory: () => void;
  isQueryLoading: boolean;
  onClearQueryRequest: () => void;
}
const QueryTargets: FC<QueryTargetsProps> = ({
  entityDragging,
  onItemDragToggle,
  onItemDropped,
  onRequireMissingItemRequest,
  onQueryPreviewRequest,
  onClearQueryRequest,
  isQueryPreviewVisible,
  isStanleyQueryPanelVisible,
  isStanleyHistoryView,
  onStanleyQueryPanelToggle,
  onOpenStanleyHistory,
  isQueryLoading,
}) => {
  const [isMoreActionsOpened, setIsMoreActionsOpened] = useState({});
  const dataSource = useSelector((state: any) => state.queryBuilder.dataSource);
  const queryBuilderType = useSelector(
    (state: any) => state.queryBuilder.queryBuilderType
  );
  const target_selected = useSelector(
    (state: any) => state.queryBuilder.target_selected
  );
  const isCompareQuery = useSelector(
    (state: any) => state.queryBuilder.isCompareQuery
  );
  const targets = useSelector(getTargets);
  const targetIds = useSelector(getTargetsIds);
  const dispatch = useDispatch();

  const tabActions = useMemo(() => {
    const selectedTarget = targets.find(
      (target) => target.target_id === target_selected
    );
    const colorOptions = ALL_QUERY_BUILDER_TAB_COLORS.map((color) => (
      <ListItem
        isActive={false}
        onClick={() => {
          dispatch(changeTargetColor(target_selected, color.key));
          setIsMoreActionsOpened({
            ...isMoreActionsOpened,
            [target_selected]: false,
          });
        }}
      >
        <StyledTabActionColor>
          <StyledColorCircle color={color.key} />
          <StyledColorLabel>
            <Text tag="span" size="xbody">
              {color.label}
            </Text>
          </StyledColorLabel>
        </StyledTabActionColor>
      </ListItem>
    ));
    const list = [
      <ListItem
        iconLeft="show"
        text="Preview"
        isActive={false}
        onClick={() => {
          onQueryPreviewRequest();
          setIsMoreActionsOpened({
            ...isMoreActionsOpened,
            [target_selected]: false,
          });
        }}
      />,
      <ListItem
        iconLeft="delete"
        isDisabled={
          (selectedTarget && selectedTarget.target_disabled) ||
          targets.length === 1
        }
        isActive={false}
        text="Remove"
        onClick={() => {
          dispatch(removeTarget(target_selected));
          setIsMoreActionsOpened({
            ...isMoreActionsOpened,
            [target_selected]: false,
          });
        }}
      />,
    ];
    list.push(...colorOptions);
    return list;
  }, [
    onQueryPreviewRequest,
    isMoreActionsOpened,
    dispatch,
    target_selected,
    targets,
  ]);

  const target = targets[0];
  if (queryBuilderType === 'conversation' && target) {
    return (
      <StyledConversationTarget>
        <QueryTarget
          target={target}
          onItemDragToggle={onItemDragToggle}
          onItemDropped={onItemDropped}
          onRequireMissingItemRequest={onRequireMissingItemRequest}
          entityDragging={entityDragging}
          isQueryPreviewVisible={isQueryPreviewVisible}
          isStanleyQueryPanelVisible={isStanleyQueryPanelVisible}
          isStanleyHistoryView={isStanleyHistoryView}
          onStanleyQueryPanelToggle={onStanleyQueryPanelToggle}
          onOpenStanleyHistory={onOpenStanleyHistory}
          isQueryLoading={isQueryLoading}
          onPreviewQueryRequest={onQueryPreviewRequest}
          onClearQueryRequest={onClearQueryRequest}
        />
      </StyledConversationTarget>
    );
  }
  return (
    <HorizontalTabs
      selectedTab={targetIds.indexOf(target_selected)}
      onTabChange={(tab) => {
        dispatch(changeTargetSelected(targetIds[tab]));
      }}
      button={
        targets.length < 3 &&
        !isCompareQuery &&
        isAvailableAddon(APPS_MAP.COMPARE) &&
        supportsSegmentationAndCompare(dataSource) && (
          <Tooltip
            content="New Audience to Compare"
            position="top"
            full
            maxWidth="medium"
          >
            <TrialRestricted feature={TRIAL_FEATURES.QUERY_BUILDER_COMPARE}>
              <StyledButton
                icon="add"
                size="small"
                onClick={() => dispatch(addNewTarget())}
                disabled={targetIds.length === 3}
                className="w-10 h-10"
                style={{ paddingRight: '6px' }}
              >
                <Text style={{ display: 'none' }}>Add new target</Text>
              </StyledButton>
            </TrialRestricted>
          </Tooltip>
        )
      }
    >
      {targets.map((target, index) => (
        <HorizontalTab
          key={`target-${index}`}
          title={
            <QueryTargetTitle
              title={target.target_label}
              color={target.target_color}
              isDisabled={target.target_disabled}
              onTitleChange={(value) =>
                dispatch(changeTargetTitle(target.target_id, value))
              }
              textTitle={
                isEmpty(target.target_label)
                  ? `Target audience ${index + 1}`
                  : target.target_label
              }
              size={
                target.target_audience_size
                  ? numberWithCommas(target.target_audience_size)
                  : '0'
              }
              isLoading={target.isLoadingMembership}
              selected={target.target_id === target_selected}
            />
          }
          color={target.target_color}
          actions={tabActions}
          isMoreActionsOpened={isMoreActionsOpened[target.target_id]}
          onMoreActionsOpen={(value) =>
            setIsMoreActionsOpened({
              ...isMoreActionsOpened,
              [target.target_id]: value,
            })
          }
        >
          <QueryTarget
            target={target}
            onItemDragToggle={onItemDragToggle}
            onItemDropped={onItemDropped}
            onRequireMissingItemRequest={onRequireMissingItemRequest}
            entityDragging={entityDragging}
            isQueryPreviewVisible={isQueryPreviewVisible}
            isStanleyQueryPanelVisible={isStanleyQueryPanelVisible}
            isStanleyHistoryView={isStanleyHistoryView}
            onStanleyQueryPanelToggle={onStanleyQueryPanelToggle}
            onOpenStanleyHistory={onOpenStanleyHistory}
            isQueryLoading={isQueryLoading}
            onPreviewQueryRequest={onQueryPreviewRequest}
            onClearQueryRequest={onClearQueryRequest}
          />
        </HorizontalTab>
      ))}
    </HorizontalTabs>
  );
};

export default QueryTargets;
